import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import { Divider, Form, Input, Space, Spin, Upload } from 'antd';
import React, { useEffect, useState } from 'react';
import { NUMBER_FIELD_TYPES } from '../../../../../common/constants';
import {
  formValidatorRules,
  getBase64,
  openNotification,
  validateImage,
  validateVideo,
} from '../../../../../common/utils';
import CommonPreview from '../../../../../components/CommonPreview';
import UnitInspectionForm from '../../../../../components/UnitScan/UnitInspectionForm';
import {
  getChecklistKey,
  getContentKey,
  getInspectionErrors,
  getNotDamagedErrors,
  isInspectionDamaged,
} from '../../../../../components/UnitScan/utils';
import ModalComponent from '../../../../../components/ModalComponent';
import NumberComponent from '../../../../../components/NumberComponent';
import ContentMasterSelect from './ContentMasterSelect';
import { TOY_DAMAGE_INSPECTION_FORM } from '../graphql/Queries';

const { Item } = Form;
const { TextArea } = Input;
const { whitespaceNotAllowed, required } = formValidatorRules;

const EMPTY_INSPECTION_FORM = { requiredContents: [], checklist: [] };

const ReportDamageModal = ({
  isModalOpen,
  setIsModalOpen,
  isBtnLoading,
  onFinish,
  damageToyImage,
  setDamageToyImage,
  form,
  toyVideo,
  setToyVideo,
  toyPreviewImage,
  setToyPreviewImage,
  toyPreviewVideo,
  setToyPreviewVideo,
  order,
  setOrder,
  showInspectionForm = false,
}) => {
  const [validationTriggered, setValidationTriggered] = useState(false);
  const [visible, setVisible] = useState(false);
  const [inspectionForm, setInspectionForm] = useState(EMPTY_INSPECTION_FORM);
  const [inspectionLoadStatus, setInspectionLoadStatus] = useState('idle');
  const [pieceCounts, setPieceCounts] = useState({});
  const [checklistAnswers, setChecklistAnswers] = useState({});
  const [inspectionErrors, setInspectionErrors] = useState({});
  const [isNotDamaged, setIsNotDamaged] = useState(false);

  const resetInspectionState = () => {
    setInspectionForm(EMPTY_INSPECTION_FORM);
    setPieceCounts({});
    setChecklistAnswers({});
    setInspectionErrors({});
    setIsNotDamaged(false);
  };

  const [getToyInspectionForm, { loading: isInspectionFormLoading }] =
    useLazyQuery(TOY_DAMAGE_INSPECTION_FORM, {
      fetchPolicy: 'network-only',
      onCompleted: (response) => {
        const data = response?.toyAdmin;
        if (
          !data ||
          !Array.isArray(data?.requiredContents) ||
          !Array.isArray(data?.checklist)
        ) {
          setInspectionLoadStatus('error');
          openNotification(
            'error',
            'Could not load toy inspection data. Close and reopen the form to try again.',
          );
          return;
        }
        const contents = (data?.requiredContents || []).filter(
          (content) => !content?.isArchived,
        );
        const checklist = (data?.checklist || []).filter(
          (item) => !item?.isArchived,
        );
        setInspectionForm({ requiredContents: contents, checklist });
        setPieceCounts({});
        setChecklistAnswers({});
        setInspectionErrors({});
        setInspectionLoadStatus('loaded');
      },
      onError: (error) => {
        setInspectionLoadStatus('error');
        openNotification('error', error?.message || 'Could not load toy inspection data.');
      },
    });

  const handleToyImageChange = (info) => {
    validateImage(info, form, setDamageToyImage, 'damageToyImage');
  };

  const handleVideoChange = async (info) => {
    const res = await validateVideo(info, form, setToyVideo, 'video');
    if (res) {
      setToyPreviewVideo(
        URL.createObjectURL(info?.fileList?.[0]?.originFileObj),
      );
    }
  };

  const handlePreview = async (fileObj) => {
    let preview;
    if (!fileObj?.url && !fileObj?.preview) {
      preview = await getBase64(fileObj?.originFileObj);
    }
    setVisible(true);
    setToyPreviewImage(fileObj?.url || preview);
  };

  useEffect(() => {
    form?.setFieldsValue({
      includedContentMasters: order?.includedContents?.map((content) => ({
        id: content?.id,
        quantity: content?.quantity,
      })),
    });
  }, [order]);

  useEffect(() => {
    if (isModalOpen && showInspectionForm && order?.orderItem?.toy?.id) {
      setInspectionLoadStatus('loading');
      resetInspectionState();
      getToyInspectionForm({
        variables: { where: { id: order?.orderItem?.toy?.id } },
      });
    }
    if (!isModalOpen) {
      resetInspectionState();
      setInspectionLoadStatus('idle');
    }
  }, [
    getToyInspectionForm,
    isModalOpen,
    order?.orderItem?.toy?.id,
    showInspectionForm,
  ]);

  const validateInspection = () => {
    if (!showInspectionForm) return true;
    const errors = getInspectionErrors(
      inspectionForm,
      pieceCounts,
      checklistAnswers,
    );
    if (Object.keys(errors)?.length > 0) {
      setInspectionErrors(errors);
      setIsNotDamaged(false);
      return false;
    }
    const hasInspectionItems =
      inspectionForm?.requiredContents?.length > 0 ||
      inspectionForm?.checklist?.length > 0;
    if (
      hasInspectionItems &&
      !isInspectionDamaged(inspectionForm, pieceCounts, checklistAnswers)
    ) {
      const notDamagedErrors = getNotDamagedErrors(
        inspectionForm,
        pieceCounts,
        checklistAnswers,
      );
      if (Object.keys(notDamagedErrors)?.length === 0) {
        openNotification(
          'error',
          'This toy does not meet the damage criteria. Please use Confirm Return instead.',
        );
      }
      setInspectionErrors(notDamagedErrors);
      setIsNotDamaged(true);
      return false;
    }
    setInspectionErrors({});
    setIsNotDamaged(false);
    return true;
  };

  const clearInspectionError = (key, value) => {
    if (isNotDamaged) {
      // Any change can make the toy qualify, so drop the whole set.
      setInspectionErrors({});
      setIsNotDamaged(false);
      return;
    }
    if (value === undefined || value === null) return;
    setInspectionErrors((prev) => {
      if (!prev?.[key]) return prev;
      const { [key]: removed, ...rest } = prev;
      return rest;
    });
  };

  const handleFinish = (values) => {
    if (showInspectionForm && inspectionLoadStatus !== 'loaded') {
      openNotification(
        'error',
        'Toy inspection data is not ready. Close and reopen the form to try again.',
      );
      return;
    }
    if (!validateInspection()) return;

    const requiredContents = showInspectionForm
      ? inspectionForm?.requiredContents?.map((content) => ({
          name: content?.name,
          minimumQuantity: content?.minimumQuantity,
          maximumQuantity: content?.maximumQuantity,
          countPresent: Number(pieceCounts?.[getContentKey(content)]) || 0,
        }))
      : undefined;
    const checklist = showInspectionForm
      ? inspectionForm?.checklist?.map((item) => ({
          text: item?.text,
          type: item?.type,
          expected: item?.expected,
          value: checklistAnswers?.[getChecklistKey(item)],
        }))
      : undefined;
    onFinish({ ...values, requiredContents, checklist });
  };

  return (
    <>
      <CommonPreview
        visible={visible}
        setVisible={setVisible}
        image={toyPreviewImage}
        setImage={setToyPreviewImage}
      />
      <ModalComponent
        title="Report Damage"
        okText="Save"
        cancelText="Cancel"
        open={isModalOpen}
        width={880}
        onOk={form?.submit}
        onCancel={() => {
          setIsModalOpen(false);
          setOrder({});
          setDamageToyImage([]);
          setToyVideo([]);
          setToyPreviewVideo();
          resetInspectionState();
          form?.resetFields();
        }}
        confirmLoading={isBtnLoading || isInspectionFormLoading}
        okButtonProps={{
          disabled:
            showInspectionForm && inspectionLoadStatus !== 'loaded',
        }}
        className="modal report-damage-modal"
      >
        <Divider className="report-damage-divider" />
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          onFinishFailed={() => {
            setValidationTriggered(true);
            validateInspection();
          }}
          validateTrigger={validationTriggered ? 'onChange' : 'onSubmit'}
        >
          {showInspectionForm && inspectionLoadStatus === 'loading' && (
            <div className="unit-scan-loading">
              <Spin />
            </div>
          )}
          {showInspectionForm && inspectionLoadStatus === 'loaded' && (
            <div className="report-damage-inspection">
              <UnitInspectionForm
                inspectionForm={inspectionForm}
                pieceCounts={pieceCounts}
                onPieceCountChange={(key, value) => {
                  setPieceCounts((prev) => ({ ...prev, [key]: value }));
                  clearInspectionError(key, value);
                }}
                checklistAnswers={checklistAnswers}
                onChecklistAnswerChange={(key, value) => {
                  setChecklistAnswers((prev) => ({ ...prev, [key]: value }));
                  clearInspectionError(key, value);
                }}
                errors={inspectionErrors}
                disabled={isBtnLoading}
                showNotes={false}
              />
            </div>
          )}
          <Item
            name="description"
            label="Damage Description"
            rules={[
              { required: true, message: 'Please Enter Damage Description!' },
              whitespaceNotAllowed,
            ]}
          >
            <TextArea
              rows={4}
              allowClear
              placeholder="Enter Damage Description"
              showCount
              maxLength={700}
            />
          </Item>
          <Item
            name="damageToyImage"
            label="Upload Images"
            rules={[{ required: true, message: 'Please Upload Images!' }]}
          >
            <Upload
              name="avatar"
              listType="picture-card"
              maxCount={4}
              fileList={damageToyImage}
              beforeUpload={() => false}
              onPreview={handlePreview}
              onChange={handleToyImageChange}
              className="avatar-uploader"
              showUploadList={{
                showDownloadIcon: false,
                showPreviewIcon: true,
                showRemoveIcon: true,
              }}
            >
              {damageToyImage?.length !== 4 && <PlusOutlined />}
            </Upload>
          </Item>
          <Item label="Video" name="video">
            {!toyPreviewVideo && !toyVideo?.url ? (
              <Upload
                listType="picture-card"
                maxCount={1}
                fileList={toyVideo}
                beforeUpload={() => false}
                onChange={handleVideoChange}
                showUploadList={{
                  showDownloadIcon: false,
                  showPreviewIcon: false,
                  showRemoveIcon: false,
                }}
              >
                {toyVideo?.length !== 1 && <PlusOutlined />}
              </Upload>
            ) : (
              <>
                <div className="toy-video-container mb-10">
                  <video
                    width="100%"
                    height="100%"
                    controls
                    src={toyPreviewVideo || toyVideo?.url}
                    className="toy-video-upload"
                  >
                    <track kind="captions" />
                  </video>
                  <DeleteOutlined
                    className="toy-video-delete-icon"
                    onClick={() => {
                      setToyVideo([]);
                      setToyPreviewVideo();
                    }}
                  />
                </div>
              </>
            )}
          </Item>
          {order?.includedContents?.length > 0 && (
            <Item label="Content Master" className="content-master-form-list">
              <Form.List name="includedContentMasters">
                {(fields) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <Space
                        key={key}
                        align="baseline"
                        className="content-master-wrapper"
                      >
                        <Space.Compact>
                          <Form.Item
                            {...restField}
                            name={[name, 'id']}
                            rules={[
                              {
                                ...required,
                                message: 'Please Select Content Master!',
                              },
                            ]}
                          >
                            <ContentMasterSelect
                              disabled
                              contentMastersData={order?.includedContents}
                            />
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            name={[name, 'quantity']}
                            rules={[
                              {
                                required: true,
                                message: 'Please Enter Quantity!',
                              },
                            ]}
                            getValueFromEvent={(event) => {
                              const { value } = event.target;
                              const parsedValue = parseInt(value, 10);
                              return Number.isNaN(parsedValue)
                                ? 0
                                : parsedValue;
                            }}
                          >
                            <NumberComponent
                              allowClear
                              isFractional={false}
                              placeholder="Enter Quantity"
                              type={NUMBER_FIELD_TYPES?.QUANTITY}
                            />
                          </Form.Item>
                        </Space.Compact>
                      </Space>
                    ))}
                  </>
                )}
              </Form.List>
            </Item>
          )}
        </Form>
      </ModalComponent>
    </>
  );
};

export default ReportDamageModal;
